import { useState } from 'react'
import styles from './controls.module.css'
import CountPill from './CountPill'
import ToggleRow from './ToggleRow'
import { SPEC_CHIP_VISIBLE } from 'lib/dealhunt/constants'
import type { FilterOption, FilterPillGroup } from 'lib/dealhunt/types'

interface PillsControlProps {
  group: FilterPillGroup
  onOption: (option: FilterOption, on: boolean) => void
}

/** Multi-select (`pills`) or single-select (`pillsOne`) chips, plus optional
 *  companion toggles. Both wrap, carry their disjunctive count and fade at zero;
 *  single-select differs only in semantics (radio, every option always visible) —
 *  a segmented control can't hold 4–5 multi-word options in a 260 px rail. */
const PillsControl: React.VFC<PillsControlProps> = ({ group, onOption }) => {
  const [expanded, setExpanded] = useState(false)
  const single = group.control === 'pillsOne'
  const visible = expanded || single ? group.options : group.options.slice(0, SPEC_CHIP_VISIBLE)
  const hidden = group.options.length - visible.length

  return (
    <>
      <div
        className={styles.pills}
        role={single ? 'radiogroup' : undefined}
        aria-label={single ? group.label : undefined}
      >
        {visible.map(opt => (
          <CountPill
            key={opt.value}
            label={opt.label}
            count={opt.count}
            selected={opt.selected}
            kind={single ? 'radio' : 'toggle'}
            onClick={() => onOption(opt, !opt.selected)}
          />
        ))}
        {hidden > 0 && (
          <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
            {`+${hidden} more`}
          </button>
        )}
        {expanded && group.options.length > SPEC_CHIP_VISIBLE && (
          <button type="button" className={styles.showMore} onClick={() => setExpanded(false)}>
            {'Show fewer'}
          </button>
        )}
      </div>
      {group.extras && group.extras.length > 0 && (
        <div>
          {group.extras.map(opt => (
            <ToggleRow
              key={`${opt.param.key}:${opt.value}`}
              label={opt.label}
              count={opt.count}
              selected={opt.selected}
              onClick={() => onOption(opt, !opt.selected)}
            />
          ))}
        </div>
      )}
    </>
  )
}

export default PillsControl
